import React, { createContext, useContext, useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";

export type AppRole = "fbaccount" | "backoffice";

export interface RuntimeConfiguration {
  apiBaseUrl: string;
  cdmBaseUrl: string;
  customerWalletsBaseUrl: string;
  env: string;
  featureCdmEnabled: boolean;
  featureCustomerWalletsEnabled: boolean;
  featureEmbeddedWalletEnrollmentEnabled: boolean;
  featureQuickSearchEnabled: boolean;
  featureSwapEnabled: boolean;
  keycloakClientId: string;
  keycloakRealm: string;
  keycloakUrl: string;
  privyAppId: string;
  privyClientId: string;
  releaseVersion: string;
  appUrl: string;
  backofficeUrl: string;
  appRole: AppRole;
  networks: {
    [key: string]: string;
  };
}

const RuntimeConfigurationContext = createContext<RuntimeConfiguration | null>(null);

export const useRuntimeConfiguration = (): RuntimeConfiguration => {
  const configuration = useContext(RuntimeConfigurationContext);
  if (!configuration) {
    throw new Error("Configuration not loaded");
  }
  return configuration;
};

interface RuntimeConfigurationProviderProps {
  children: React.ReactNode;
}

/**
 * Loads runtime configuration from static file (in /public folder).
 */
export const loadRuntimeConfiguration = async (): Promise<RuntimeConfiguration> => {
  const response = await fetch("/config.json");
  if (!response.ok) {
    throw new Error(`Failed to load application configuration (${response.status})`);
  }
  const value = await response.json();
  const featureEmbeddedWalletEnrollmentEnabled = value.FEATURE_EMBEDDED_WALLET_ENROLLMENT_ENABLED ?? false;
  const privyAppId = typeof value.PRIVY_APP_ID === "string" ? value.PRIVY_APP_ID.trim() : "";
  const privyClientId = typeof value.PRIVY_CLIENT_ID === "string" ? value.PRIVY_CLIENT_ID.trim() : "";

  if (featureEmbeddedWalletEnrollmentEnabled && (!privyAppId || !privyClientId)) {
    throw new Error("Enabled embedded wallet enrollment requires PRIVY_APP_ID and PRIVY_CLIENT_ID");
  }

  return {
    apiBaseUrl: value.API_BASE_URL,
    cdmBaseUrl: value.CDM_BASE_URL,
    customerWalletsBaseUrl: value.CUSTOMER_WALLETS_BASE_URL ?? "",
    env: value.ENV ?? "",
    featureCdmEnabled: value.FEATURE_CDM_ENABLED ?? false,
    featureCustomerWalletsEnabled: value.FEATURE_CUSTOMER_WALLETS_ENABLED ?? false,
    featureEmbeddedWalletEnrollmentEnabled,
    featureQuickSearchEnabled: value.FEATURE_QUICK_SEARCH_ENABLED ?? false,
    featureSwapEnabled: value.FEATURE_SWAP_ENABLED ?? false,
    keycloakRealm: value.KEYCLOAK_REALM,
    keycloakClientId: value.KEYCLOAK_CLIENT_ID,
    keycloakUrl: value.KEYCLOAK_URL,
    privyAppId,
    privyClientId,
    appUrl: value.APP_URL,
    backofficeUrl: value.BACKOFFICE_URL,
    appRole: value.APP_ROLE === "backoffice" ? "backoffice" : "fbaccount",
    releaseVersion: value.RELEASE_VERSION,
    networks: value.NETWORKS,
  };
};

export const RuntimeConfigurationProvider: React.FC<RuntimeConfigurationProviderProps> = ({ children }) => {
  const [runtimeConfig, setRuntimeConfig] = useState<RuntimeConfiguration | null>(null);
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    loadRuntimeConfiguration().then(setRuntimeConfig).catch(showBoundary);
  }, [showBoundary]);

  return (
    <RuntimeConfigurationContext.Provider value={runtimeConfig}>
      {runtimeConfig ? children : <div>&nbsp;</div>}
    </RuntimeConfigurationContext.Provider>
  );
};
